import { MapStore } from "flux/utils"
import Immutable, { fromJS } from "immutable"
import Dispatcher from "../Dispatcher"
import { filterableResourceTypeNames } from "../scripts/resourceTypes"
import { licenseNames } from "../scripts/licenseTypes"
import {
  SET_FROM_TOKEN,
  ADD_SEARCH_FILTER_CONTROL,
  APPEND_SEARCH_FILTER,
  REMOVE_SEARCH_FILTER,
  SET_PER_PAGE,
  SET_ARTICLE_STYLE,
  DEFAULT_ARTICLE_STYLE,
  LOAD_PREFERENCES,
  SEARCH_SORT,
  SEARCH_SCROLL_POSITION,
  SEARCH_LOAD_MORE,
  SEARCH_LOADING,
  SHOW_SEARCH_FILTERS,
  DEFAULT_PAGE_SIZE,
  DEFAULT_PER_PAGE,
  SET_STATE_FROM_PARAMS,
  SET_RESULT_IDS,
  DEFAULT_LOCALE,
  SET_LOCALE,
  SET_BOUNDING_BOX,
  SET_ACTIVE_COLLECTIONS
} from "../constants"

const initialState = Immutable.fromJS({
  locale: DEFAULT_LOCALE,
  q: "",
  sort: null,
  searchMap: false,
  defaultSort: "updated",
  pageSize: DEFAULT_PAGE_SIZE,
  perPage: DEFAULT_PER_PAGE,
  viewHasChrome: true,
  viewDisableHeaderPad: false,
  viewDisableFooter: false,
  headerHidden: false,
  searchAllTypes: true,
  searchAllLicenses: true,
  articleStyle: DEFAULT_ARTICLE_STYLE,
  showDisplayOptions: false,
  showSearchFilters: true,
  reflowResults: false,
  doQuery: false,
  navExpanded: false,
  searchLoading: false,
  viewSize: "large",
  maybeQuery: false,
  searchFocused: false,
  searchScrollPos: 0,
  tokenString: "",
  loggedIn: false,
  userRole: null,
  userLogin: null,
  usersName: null,
  userId: null,
  userRoles: [],
  filters: {},
  breadcrumbs: {},
  dates: {},
  resultIds: [],
  notificationVisible: false,
  browserSupportVisible: false,
  collectionsIds: []
}).merge({
  queryTerms: Immutable.OrderedSet(),
  searchTypes: Immutable.OrderedSet(),
  searchTags: Immutable.Set(),
  filterControls: Immutable.OrderedSet()
})

class State extends MapStore {
  getInitialState() {
    return initialState
  }

  getStateJS() {
    return this.getState().toJS()
  }

  _action = (state, action) => {
    switch (action.type) {
      case SET_LOCALE:
        return state.set("locale", action.locale)

      case SET_RESULT_IDS:
        return state.set("resultIds", action.ids)

      case LOAD_PREFERENCES:
        return action.data ? state.merge(fromJS(action.data)) : state

      case SET_PER_PAGE: {
        return state.withMutations(ctx => {
          const pageSize = state.get("pageSize")
          ctx.set("perPage", action.perPage)

          if (action.perPage > pageSize) {
            ctx.set("pageSize", action.perPage).set("maybeQuery", true)
          }
        })
      }

      case SET_ACTIVE_COLLECTIONS: {
        return state.set(action.collectionsIds)
      }

      case SET_FROM_TOKEN:
        return state.merge(fromJS(action.payload))

      case ADD_SEARCH_FILTER_CONTROL:
        return state.set(
          "filterControls",
          state.get("filterControls").add(action.name)
        )

      case APPEND_SEARCH_FILTER:
        return state.withMutations(ctx => {
          const { name, value } = action
          const key = ["filters", name]
          const existing = ctx.getIn(key)
          return (existing
            ? ctx.setIn(key, existing.add(value))
            : ctx.setIn(key, Immutable.Set([value]))
          ).set("maybeQuery", true)
        })

      case "append-search-filter-without-query":
        return state.withMutations(ctx => {
          const { name, value } = action
          const key = ["filters", name]

          return ctx
            .set("filters", Immutable.Map())
            .set("breadcrumbs", Immutable.Map())
            .set("dates", Immutable.Map())
            .setIn(key, Immutable.Set([value]))
        })

      case REMOVE_SEARCH_FILTER:
        return state.withMutations(ctx => {
          const { name, value } = action
          const key = ["filters", name]
          return ctx
            .setIn(key, ctx.getIn(key).delete(value))
            .set("maybeQuery", true)
        })

      case SET_ARTICLE_STYLE: {
        const style = action.articleStyle
        const searchMap = style === "map"
        return state.withMutations(ctx =>
          ctx
            .set("articleStyle", style)
            .set("maybeQuery", state.get("searchMap") !== searchMap)
            .set("searchMap", searchMap)
        )
      }

      case "dropdown-display-options":
        return state.set("showDisplayOptions", action.show)

      case "search":
        return state.withMutations(ctx =>
          ctx
            .set("q", action.q)
            .set("defaultSort", action.q ? "relevance" : "updated")
            .set("queryTerms", Immutable.OrderedSet())
            .set("maybeQuery", true)
        )

      case "clear-search-term": {
        return state.withMutations(ctx =>
          ctx
            .set("q", "")
            .set("defaultSort", "updated")
            .set("queryTerms", Immutable.OrderedSet())
            .set("maybeQuery", true)
        )
      }

      case "clear-breadcrumbs-term": {
        return state.withMutations(ctx => {
          const { name } = action
          const key = ["breadcrumbs", name]

          return ctx.setIn(key, "").set("maybeQuery", true)
        })
      }

      case "set-breadcrumbs-term": {
        return state.withMutations(ctx => {
          const { name, breadcrumbsSearch } = action
          const key = ["breadcrumbs", name]

          return ctx.setIn(key, breadcrumbsSearch).set("maybeQuery", true)
        })
      }

      case "set-breadcrumbs-term-without-query": {
        return state.withMutations(ctx => {
          const { name, breadcrumbsSearch } = action
          const key = ["breadcrumbs", name]

          return ctx
            .set("filters", Immutable.Map())
            .set("breadcrumbs", Immutable.Map())
            .set("dates", Immutable.Map())
            .setIn(key, breadcrumbsSearch)
        })
      }

      case SEARCH_LOADING:
        return state.set("searchLoading", action.loading)

      case SHOW_SEARCH_FILTERS:
        return state.set("showSearchFilters", action.show)

      case "toggle-search-filters":
        return state.set("showSearchFilters", !state.get("showSearchFilters"))

      case "toggle-nav":
        return state.set("navExpanded", !state.get("navExpanded"))

      case "close-nav":
        return state.set("navExpanded", false)

      case "leave-page":
        return state.merge({
          navExpanded: false,
          searchFocused: false
        })

      case "search-filter-type":
        return state.withMutations(ctx =>
          ctx
            .set("maybeQuery", true)
            .set("searchAllTypes", false)
            .update(
              "searchTypes",
              types =>
                action.data.active
                  ? types.add(action.data.type)
                  : types.delete(action.data.type)
            )
        )

      case "search-filter-license":
        return state.withMutations(ctx => {
          const { active, type } = action.data
          const key = ["filters", "license"]
          const existing = ctx.getIn(key)

          if (existing) {
            return (active
              ? ctx.setIn(key, existing.add(type))
              : ctx.setIn(key, existing.delete(type))
            )
              .set("searchAllLicenses", false)
              .set("maybeQuery", true)
          } else {
            return ctx
              .setIn(key, Immutable.Set([type]))
              .set("searchAllLicenses", false)
              .set("maybeQuery", true)
          }
        })

      case "search-filter-tags":
        return state.withMutations(ctx =>
          ctx
            .set("maybeQuery", true)
            .set("searchTags", Immutable.OrderedSet(action.values || []))
        )

      case "search-filter-add-tag":
        return state.withMutations(ctx =>
          ctx
            .set("maybeQuery", true)
            .update("searchTags", tags => tags.add(action.tag))
        )

      case "search-filter-remove-tag":
        return state.withMutations(ctx =>
          ctx
            .set("maybeQuery", true)
            .update("searchTags", tags => tags.delete(action.tag))
        )

      case "search-filters-type-toggle":
        return state.merge({
          maybeQuery: true,
          searchTypes: Immutable.OrderedSet(
            action.value ? filterableResourceTypeNames() : []
          )
        })

      case "search-filters-license-toggle":
        return state.withMutations(ctx => {
          const { value } = action
          const key = ["filters", "license"]

          return ctx
            .setIn(key, Immutable.OrderedSet(value ? licenseNames : []))
            .set("searchAllLicenses", true)
            .set("maybeQuery", true)
        })

      case SEARCH_LOAD_MORE: {
        return state.merge({
          maybeQuery: true,
          pageSize: state.get("pageSize") + state.get("perPage")
        })
      }

      case SEARCH_SORT:
        return state.merge({
          maybeQuery: true,
          sort: action.sort
        })

      case "add-query-term":
        return state.withMutations(ctx =>
          ctx
            .set("maybeQuery", true)
            .update("queryTerms", terms => terms.add(action.term))
        )

      case "remove-query-term":
        return state.withMutations(ctx =>
          ctx
            .set("maybeQuery", true)
            .update("queryTerms", terms => terms.delete(action.term))
        )

      case SET_STATE_FROM_PARAMS:
        const { params } = action

        // Called by componentDidMount of Search.js params here are defined in routes.js
        return state.withMutations(ctx =>
          ctx
            // .set("maybeQuery", true)
            .set("q", params.q)
            .set("searchMap", !!params.map)
            .set("sort", params.sort)
            .set("defaultSort", params.q ? "relevance" : "updated")
            .set("queryTerms", Immutable.OrderedSet(params.terms))
            .set("searchTags", Immutable.Set(params.tags))
            .set("searchTypes", Immutable.OrderedSet(params.types))
            .set("pageSize", params.total || DEFAULT_PAGE_SIZE)
            .set("perPage", params.perPage || DEFAULT_PER_PAGE)
            .set(
              "filters",
              Object.keys(params.filters).reduce(
                (m, k) => m.set(k, Immutable.Set(params.filters[k])),
                Immutable.Map()
              )
            )
            .set(
              "breadcrumbs",
              Object.keys(params.breadcrumbs).reduce(
                (m, k) => m.set(k, Immutable.Set(params.breadcrumbs[k])),
                Immutable.Map()
              )
            )
            .set(
              "dates",
              Object.keys(params.dates).reduce(
                (m, k) => m.set(k, Immutable.Set(params.dates[k])),
                Immutable.Map()
              )
            )
        )

      case SET_BOUNDING_BOX:
        return state.merge({ locationBBox: action.bbox, maybeQuery: true })

      case "set-search-bounding-box":
        return state.merge({
          locationBBox: action.bbox,
          maybeQuery: true,
          searchMap: action.map
        })

      case "search-focused":
        return state.set("searchFocused", action.focused)

      case "search-date-range": {
        return state.withMutations(ctx => {
          const { name, from, to } = action
          const key = ["dates", name]
          const existing = state.getIn(key)

          let searchDateFrom = existing ? existing.from : null
          let searchDateTo = existing ? existing.to : null

          if ("from" in action) {
            searchDateFrom = from
          }
          if ("to" in action) {
            searchDateTo = to
          }

          const fromTimestamp = Date.parse(searchDateFrom)
          const toTimestap = Date.parse(searchDateTo)

          if (fromTimestamp && toTimestap && fromTimestamp > toTimestap) {
            searchDateTo = searchDateFrom
          }

          const newDate = { from: searchDateFrom, to: searchDateTo }

          return ctx.setIn(key, newDate).set("maybeQuery", true)
        })
      }

      case SEARCH_SCROLL_POSITION:
        return state.set("searchScrollPos", action.pos)

      case "search-reset":
        return state.withMutations(ctx =>
          ctx
            .set("maybeQuery", true)
            .set("q", "")
            .set("searchAllTypes", true)
            .set("searchAllLicenses", true)
            .set("searchScrollPos", 0)
            .set("sort", "relevance")
            .set("pageSize", null)
            .set("queryTerms", Immutable.OrderedSet())
            .set("searchTags", Immutable.Set())
            .set("searchTerms", Immutable.OrderedSet())
        )

      case "view-size":
        return state.set("viewSize", action.data.name)

      case "view-has-chrome":
        return state.set("viewHasChrome", action.value)

      case "view-disable-header-pad":
        return state.set("viewDisableHeaderPad", action.value)

      case "view-disable-footer":
        return state.set("viewDisableFooter", action.value)

      case "header-hidden":
        return state.set("headerHidden", action.value)

      case "state-notification-visible":
        return state.set("notificationVisible", action.value)

      case "browser-support-visible":
        return state.set("browserSupportVisible", action.value)

      default:
        return state
    }
  }

  /*
    * Goal is to set the doQuery key based on the state after an action has been applied
    */
  _determineDoQuery = state => {
    const existingState = this.getState()
    return state.merge({
      maybeQuery: false, // reset
      reflowResults:
        state.get("showSearchFilters") !==
        existingState.get("showSearchFilters"),
      doQuery:
        state.get("maybeQuery") &&
        (state.get("q") !== existingState.get("q") ||
          state.get("searchMap") !== existingState.get("searchMap") ||
          state.get("locationBBox") !== existingState.get("locationBBox") ||
          state.get("queryTerms") !== existingState.get("queryTerms") ||
          state.get("pageSize") !== existingState.get("pageSize") ||
          state.get("sort") !== existingState.get("sort") ||
          state.get("defaultSort") !== existingState.get("defaultSort") ||
          state.get("searchTypes") !== existingState.get("searchTypes") ||
          state.get("filters") !== existingState.get("filters") ||
          state.get("searchTags") !== existingState.get("searchTags") ||
          state.get("breadcrumbs") !== existingState.get("breadcrumbs") ||
          state.get("dates") !== existingState.get("dates"))
    })
  }

  /**
   * I'm not sure if this is good practice :/
   */
  _after = (state, action) => {
    return this._determineDoQuery(state)
  }

  _log = (state, title) => {
    return state
  }

  reduce(state, action) {
    return this._after(this._action(state, action), action)
    // return this._log(this._after(this._action(state, action), action), "")
    // return this._log( this._after( this._log( this._action( this._log(state, 'before'), action), 'after action '+action.type), action ), 'final');
  }

  getIn(key) {
    return this.getState().getIn(key)
  }
}

// Export a singleton instance of the store, could do this some other way if
// you want to avoid singletons.
const instance = new State(Dispatcher)
export default instance



// WEBPACK FOOTER //
// ./webapp/stores/State.js